import { CommonModule } from "@angular/common"
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core"
import { FormControl, ReactiveFormsModule } from "@angular/forms"
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from "@angular/material/autocomplete"
import { MatChipsModule } from "@angular/material/chips"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { Subscription } from "rxjs"
import { Station } from "../../models/station.model"

const MIN_SEARCH_LENGTH = 1

@Component({
  selector: "app-station-multiselect-field",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
  ],
  templateUrl: "./station-multiselect-field.component.html",
  styleUrl: "./station-multiselect-field.component.css",
})
export class StationMultiselectFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() stations: Station[] = []
  @Input() label = "Stations"
  @Input() placeholder = "Search and select stations..."
  @Input() hint = ""
  @Input() required = false
  @Input() selectedStations: Station[] = []
  @Output() selectedStationsChange = new EventEmitter<Station[]>()

  searchControl = new FormControl<string>("", { nonNullable: true })
  filteredStations: Station[] = []
  private sub?: Subscription

  ngOnInit(): void {
    this.sub = this.searchControl.valueChanges.subscribe(() => this.updateFilteredStations())
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["stations"] || changes["selectedStations"]) {
      this.updateFilteredStations()
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe()
  }

  get hasSearchQuery(): boolean {
    return this.searchControl.value.trim().length >= MIN_SEARCH_LENGTH
  }

  get searchPrompt(): string {
    return `Type at least ${MIN_SEARCH_LENGTH} character to search stations`
  }

  stationLabel(station: Station): string {
    return `${station.name} (${station.call_sign})`
  }

  onStationSelected(event: MatAutocompleteSelectedEvent): void {
    const id = Number(event.option.value)
    const station = this.stations.find((s) => s.id === id)
    if (!station || this.isSelected(station)) {
      return
    }

    event.option.deselect()
    const next = [...this.selectedStations, station]
    this.selectedStations = next
    this.selectedStationsChange.emit(next)
    this.searchControl.setValue("", { emitEvent: false })
    this.updateFilteredStations()
  }

  removeStation(station: Station): void {
    const next = this.selectedStations.filter((s) => s.id !== station.id)
    this.selectedStations = next
    this.selectedStationsChange.emit(next)
    this.updateFilteredStations()
  }

  private isSelected(station: Station): boolean {
    return this.selectedStations.some((s) => s.id === station.id)
  }

  private updateFilteredStations(): void {
    const query = this.searchControl.value.trim().toLowerCase()
    const pool = this.stations.filter((station) => !this.isSelected(station))

    if (!query) {
      this.filteredStations = pool
      return
    }

    this.filteredStations = pool.filter((station) => {
      const name = (station.name || "").toLowerCase()
      const callSign = (station.call_sign || "").toLowerCase()
      return name.includes(query) || callSign.includes(query)
    })
  }

  onPanelOpened(): void {
    this.updateFilteredStations()
  }
}
